import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { images } from '../data/mockData';

const LogisticsSection = ({ content }) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % content.logistics.steps.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible, content.logistics.steps.length]);

  return (
    <section
      id="logistics"
      ref={sectionRef}
      className="py-32 bg-slate-900 relative overflow-hidden"
    >
      {/* Animated background */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h2
            className={`text-4xl md:text-5xl font-bold text-white mb-6 transition-all duration-1000 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            {content.logistics.title}
          </h2>
          <p
            className={`text-lg text-slate-400 transition-all duration-1000 delay-100 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            {content.logistics.subtitle}
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto mb-20">
          {content.logistics.steps.map((step, index) => (
            <div
              key={index}
              className={`relative transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              } ${
                activeStep === index ? 'scale-105' : 'scale-100'
              }`}
              style={{ transitionDelay: `${index * 100 + 200}ms` }}
            >
              <div
                className={`p-8 rounded-2xl transition-all duration-500 ${
                  activeStep === index
                    ? 'bg-emerald-600 shadow-2xl shadow-emerald-600/50'
                    : 'bg-slate-800 hover:bg-slate-700'
                }`}
              >
                <div
                  className={`text-5xl font-bold mb-4 transition-colors ${
                    activeStep === index
                      ? 'text-white'
                      : 'text-slate-600'
                  }`}
                >
                  {step.step}
                </div>
                <h3
                  className={`text-xl font-semibold mb-3 transition-colors ${
                    activeStep === index
                      ? 'text-white'
                      : 'text-white'
                  }`}
                >
                  {step.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed transition-colors ${
                    activeStep === index
                      ? 'text-emerald-50'
                      : 'text-slate-400'
                  }`}
                >
                  {step.description}
                </p>
              </div>

              {/* Arrow connector */}
              {index < content.logistics.steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                  <ArrowRight
                    className={`w-8 h-8 transition-colors ${
                      activeStep === index
                        ? 'text-emerald-400'
                        : 'text-slate-700'
                    }`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Route Map */}
        <div
          className={`max-w-4xl mx-auto transition-all duration-1000 delay-500 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-12 border border-slate-700">
            <div className="flex items-center justify-center space-x-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <span className="text-2xl font-bold text-white">TR</span>
                </div>
                <p className="text-white font-semibold">{content.logistics.route.from}</p>
              </div>

              <div className="flex-1 relative">
                <div className="h-1 bg-slate-700 relative overflow-hidden rounded-full">
                  <div
                    className="absolute top-0 left-0 h-full bg-emerald-600 transition-all duration-1000"
                    style={{ width: isVisible ? '100%' : '0%' }}
                  />
                </div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="bg-slate-800 px-4 py-2 rounded-lg border border-slate-600">
                    <p className="text-slate-300 text-sm font-medium">
                      {content.logistics.route.via}
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <span className="text-2xl font-bold text-white">EU</span>
                </div>
                <p className="text-white font-semibold">{content.logistics.route.to}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Logistics Images */}
        <div
          className={`grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mt-16 transition-all duration-1000 delay-700 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          {images.logistics.slice(1, 4).map((img, index) => (
            <div
              key={index}
              className="group relative h-64 rounded-xl overflow-hidden"
            >
              <img
                src={img}
                alt={`Logistics ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LogisticsSection;
